import type { UserInfo } from '/#/store';
import { encryptPswByBase64 } from '/@/utils/cipher';
import { defineStore } from 'pinia';
import { store } from '/@/store';
import { RoleEnum } from '/@/enums/roleEnum';
import {
  loginApi,
  logOutApi,
  changeUserStatusApi,
  chooseLoginApi,
  bindUserApi,
} from '/@/api/user';
import { initOnline } from '/@/core/auth';
import { PageEnum } from '/@/enums/pageEnum';
import { ROLES_KEY, TOKEN_KEY, USER_INFO_KEY } from '/@/enums/cacheEnum';
import { OnlineStatusResultModel } from '/@/api/user/model/userModel';
import {
  LoginParamsModel,
  LoginResultModel,
  ChooseLoginParamsModel,
  BindUserParamsModel,
} from '/@/api/user/model/userModel';
import { useMessage } from '/@/hooks/web/useMessage';
import { router } from '/@/router';
import { useServiceStoreWithOut } from './service';
import { createLocalStorage } from '/@/utils/cache';
const ls = createLocalStorage();
const { createMessage } = useMessage();
interface UserState {
  userInfo: Nullable<UserInfo>;
  accessToken?: string;
  roleList: RoleEnum[];
  sessionTimeout?: boolean;
  lastUpdateTime: number;
  onlineInfo: Nullable<OnlineStatusResultModel>;
  rules?: Array<string>;
}

export const useUserStore = defineStore({
  id: 'app-user',
  state: (): UserState => ({
    // user info
    userInfo: null,
    // accessToken
    accessToken: undefined,
    // roleList
    roleList: [],
    // Whether the login expired
    sessionTimeout: false,
    // Last fetch time
    lastUpdateTime: 0,
    rules: [],
    onlineInfo: null,
  }),
  getters: {
    getRules(): Array<string> {
      return this.rules || [];
    },
    getUserInfo(): UserInfo {
      return this.userInfo || ls.get(USER_INFO_KEY) || {};
    },
    getToken(): string {
      return this.accessToken || ls.get(TOKEN_KEY);
    },
    getRoleList(): RoleEnum[] {
      return this.roleList.length > 0 ? this.roleList : ls.get(ROLES_KEY);
    },
    getSessionTimeout(): boolean {
      return !!this.sessionTimeout;
    },
    getLastUpdateTime(): number {
      return this.lastUpdateTime;
    },
  },
  actions: {
    setRules(rules: Array<string>): void {
      this.rules = rules;
    },
    setOnlineInfo(info: OnlineStatusResultModel) {
      this.onlineInfo = info;
    },
    setToken(info: string | undefined, exp: number) {
      this.accessToken = info ? info : ''; // for null or undefined value
      ls.set(TOKEN_KEY, info, exp);
    },
    setRoleList(roleList: RoleEnum[]) {
      this.roleList = roleList;
      ls.set(ROLES_KEY, roleList);
    },
    setUserInfo(info: UserInfo | null) {
      this.userInfo = info;
      this.lastUpdateTime = new Date().getTime();
      ls.set(USER_INFO_KEY, info);
    },
    setSessionTimeout(flag: boolean) {
      this.sessionTimeout = flag;
    },
    resetState() {
      this.userInfo = null;
      this.accessToken = '';
      this.roleList = [];
      this.sessionTimeout = false;
    },
    /**
     * @description: login by password, 通过账号密码登录
     */
    async loginByPassword(params: LoginParamsModel): Promise<string | null> {
      try {
        const {
          content: { accessToken, exp },
        } = await loginApi({
          ...params,
          password: encryptPswByBase64(params.password, params.username),
        });
        return this.afterLoginAction({ accessToken, exp }, false);
      } catch (error) {
        console.log('error', error);
        return Promise.reject(error);
      }
    },
    /**
     * @description: login by bind but only one, 通过只绑定一个账号直接登录
     */
    async loginByOnlyOne(
      loginResult: LoginResultModel
    ): Promise<string | null> {
      try {
        const { accessToken, exp } = loginResult;
        return this.afterLoginAction({ accessToken, exp }, true);
      } catch (error) {
        console.log('error', error);
        return Promise.reject(error);
      }
    },
    /**
     * @description: login by bind but choose one, 通过选择多个账号中的一个登录
     */
    async loginByChooseOne(
      params: ChooseLoginParamsModel
    ): Promise<string | null> {
      try {
        const {
          content: { accessToken, exp },
        } = await chooseLoginApi({ ...params });
        return this.afterLoginAction({ accessToken, exp }, true);
      } catch (error) {
        console.log('error', error);
        return Promise.reject(error);
      }
    },
    /**
     * @description: login by verify, 通过校验临时登录
     */
    async loginByVerify(params: BindUserParamsModel): Promise<string | null> {
      try {
        const {
          content: { accessToken, exp },
        } = await bindUserApi({ ...params });
        return this.afterLoginAction({ accessToken, exp }, true);
      } catch (error) {
        console.log('error', error);
        return Promise.reject(error);
      }
    },

    /**
     * @description: 登录完成
     */
    async afterLoginAction(
      loginResult: LoginResultModel,
      isOnline?: boolean
    ): Promise<string | null> {
      const { accessToken, exp } = loginResult;
      this.setToken(accessToken, exp);
      if (!this.getToken) return null;
      // get user info
      isOnline && (await this.updateLine(1));
      setTimeout(() => {
        window.location.href = '/';
        // 会触发socket连接
        // return null;
      }, 1000);
      return null;
    },
    /**
     * @description: logout
     */
    async logout(isOffline?: boolean): Promise<void | null> {
      try {
        await logOutApi();

        return this.afterLogoutAction(isOffline);
      } catch (error) {
        console.log('error', error);
        return Promise.reject(error);
      }
    },
    async afterLogoutAction(isOnline?: boolean): Promise<void | null> {
      const serviceStore = useServiceStoreWithOut();
      // offline
      isOnline && (await this.updateLine(0));
      // remove token
      ls.clear();
      router.replace({
        path: PageEnum.BASE_LOGIN,
      });
      if (serviceStore.ws) serviceStore.ws.close(false);
      return null;
    },
    /**
     * @description: change line
     */
    async updateLine(workState: number): Promise<string | null> {
      try {
        const LineText = ['下线', '上线'];
        // await changeUserStatusApi({ workState });
        await initOnline();
        createMessage.success(`${LineText[workState]}成功`);
        return Promise.resolve('');
      } catch (error) {
        console.log('error', error);
        return Promise.reject(error);
      }
    },
  },
});

// Need to be used outside the setup
export function useUserStoreWithOut() {
  return useUserStore(store);
}
